import React, { useState, useRef, useEffect } from 'react';
import { 
  Headphones, X, Play, Pause, Minimize2, Maximize2, 
  ExternalLink, Music, Youtube, ChevronDown, GripVertical,
  Volume2, VolumeX, SkipForward, SkipBack
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const MediaHub = ({ isOpen, onClose, isDark }) => {
  const [activeTab, setActiveTab] = useState('youtube');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [musicService, setMusicService] = useState('spotify');
  const [musicEmbedUrl, setMusicEmbedUrl] = useState('');
  const [loadedMusicEmbed, setLoadedMusicEmbed] = useState('');
  const [miniPlayerOpen, setMiniPlayerOpen] = useState(false);
  const [miniPlayerMinimized, setMiniPlayerMinimized] = useState(false);
  const [miniPlayerPosition, setMiniPlayerPosition] = useState({ x: window.innerWidth - 420, y: window.innerHeight - 320 });
  const [miniPlayerSize, setMiniPlayerSize] = useState({ width: 400, height: 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const miniPlayerRef = useRef(null);

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  // Load YouTube video
  const loadYoutubeVideo = () => {
    const videoId = extractYoutubeId(youtubeUrl);
    if (videoId) {
      setYoutubeVideoId(videoId);
      setYoutubeTitle(`YouTube Video`);
    }
  };

  // Load Music embed
  const loadMusicEmbed = () => {
    if (musicEmbedUrl) {
      // Convert Spotify URLs to embed format if needed
      let embedUrl = musicEmbedUrl;
      if (musicService === 'spotify' && musicEmbedUrl.includes('open.spotify.com')) {
        embedUrl = musicEmbedUrl.replace('open.spotify.com', 'open.spotify.com/embed');
      }
      setLoadedMusicEmbed(embedUrl);
    }
  };

  // Handle mini-player drag
  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - miniPlayerPosition.x,
        y: e.clientY - miniPlayerPosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setMiniPlayerPosition({
        x: Math.max(0, Math.min(window.innerWidth - miniPlayerSize.width, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - miniPlayerSize.height, e.clientY - dragOffset.y))
      });
    }
    if (isResizing) {
      const newWidth = Math.max(300, Math.min(800, e.clientX - miniPlayerPosition.x));
      const newHeight = Math.max(200, Math.min(600, e.clientY - miniPlayerPosition.y));
      setMiniPlayerSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Pop out to mini player
  const popOutPlayer = () => {
    setMiniPlayerOpen(true);
    setMiniPlayerMinimized(false);
    onClose();
  };

  // Close mini player
  const closeMiniPlayer = () => {
    setMiniPlayerOpen(false);
    setMiniPlayerMinimized(false);
    setYoutubeVideoId('');
    setLoadedMusicEmbed('');
  };

  // Get current playing content
  const hasContent = youtubeVideoId || loadedMusicEmbed;
  const currentContent = activeTab === 'youtube' ? 'YouTube' : musicService;

  return (
    <>
      {/* Main Panel */}
      {isOpen && (
        <div 
          className={`absolute top-full right-0 mt-2 z-[100] w-[400px] rounded-xl border shadow-2xl overflow-hidden ${
            isDark 
              ? 'bg-slate-900 border-slate-700' 
              : 'bg-white border-slate-200'
          }`}
          style={{
            boxShadow: isDark 
              ? '0 0 30px rgba(59, 130, 246, 0.2), 0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
              : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${
            isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-slate-50'
          }`}>
            <div className="flex items-center gap-2">
              <Headphones className="h-5 w-5 text-blue-500" />
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Media Hub
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasContent && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={popOutPlayer}
                  className={`text-xs ${isDark ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Pop Out
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={`grid grid-cols-2 m-3 ${
              isDark ? 'bg-slate-800' : 'bg-slate-100'
            }`}>
              <TabsTrigger 
                value="youtube" 
                className={`flex items-center gap-2 data-[state=active]:bg-red-600 data-[state=active]:text-white`}
              >
                <Youtube className="h-4 w-4" />
                YouTube
              </TabsTrigger>
              <TabsTrigger 
                value="music"
                className={`flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white`}
              >
                <Music className="h-4 w-4" />
                Music
              </TabsTrigger>
            </TabsList>

            {/* YouTube Tab */}
            <TabsContent value="youtube" className="p-4 pt-0">
              <div className="space-y-4">
                {/* URL Input */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste YouTube URL..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className={isDark ? 'bg-slate-800 border-slate-600' : ''}
                    onKeyDown={(e) => e.key === 'Enter' && loadYoutubeVideo()}
                  />
                  <Button onClick={loadYoutubeVideo} className="bg-red-600 hover:bg-red-700">
                    Load
                  </Button>
                </div>

                {/* Video Player Area */}
                <div className={`rounded-lg overflow-hidden ${
                  isDark ? 'bg-slate-800' : 'bg-slate-100'
                }`}>
                  {youtubeVideoId ? (
                    <div className="aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&enablejsapi=1`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="aspect-video flex flex-col items-center justify-center text-center p-6">
                      <Youtube className={`h-12 w-12 mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                      <p className={`font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        No video loaded
                      </p>
                      <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Paste a YouTube link to start playing
                      </p>
                    </div>
                  )}
                </div>

                {youtubeVideoId && (
                  <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Playing: {youtubeTitle}
                  </p>
                )}
              </div>
            </TabsContent>

            {/* Music Tab */}
            <TabsContent value="music" className="p-4 pt-0">
              <div className="space-y-4">
                {/* Service Selector */}
                <Select value={musicService} onValueChange={setMusicService}>
                  <SelectTrigger className={isDark ? 'bg-slate-800 border-slate-600' : ''}>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spotify">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500" />
                        Spotify
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube-music">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500" />
                        YouTube Music
                      </div>
                    </SelectItem>
                    <SelectItem value="soundcloud">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-orange-500" />
                        SoundCloud
                      </div>
                    </SelectItem>
                    <SelectItem value="other">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-blue-500" />
                        Other (Embed URL)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Embed URL Input */}
                <div className="flex gap-2">
                  <Input
                    placeholder={
                      musicService === 'spotify' 
                        ? "Paste Spotify embed URL..." 
                        : musicService === 'youtube-music'
                        ? "Paste YouTube Music URL..."
                        : musicService === 'soundcloud'
                        ? "Paste SoundCloud embed URL..."
                        : "Paste embed or streaming URL..."
                    }
                    value={musicEmbedUrl}
                    onChange={(e) => setMusicEmbedUrl(e.target.value)}
                    className={isDark ? 'bg-slate-800 border-slate-600' : ''}
                    onKeyDown={(e) => e.key === 'Enter' && loadMusicEmbed()}
                  />
                  <Button onClick={loadMusicEmbed} className="bg-green-600 hover:bg-green-700">
                    Load
                  </Button>
                </div>

                {/* Music Player Area */}
                <div className={`rounded-lg overflow-hidden ${
                  isDark ? 'bg-slate-800' : 'bg-slate-100'
                }`}>
                  {loadedMusicEmbed ? (
                    <div className="h-[200px]">
                      <iframe
                        src={loadedMusicEmbed}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="h-[200px] flex flex-col items-center justify-center text-center p-6">
                      <Music className={`h-12 w-12 mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                      <p className={`font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        No music loaded
                      </p>
                      <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Paste an embed link from Spotify, YouTube Music, or other streaming service
                      </p>
                    </div>
                  )}
                </div>

                {/* Quick Tips */}
                <div className={`text-xs space-y-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  <p className="font-medium">How to get embed URLs:</p>
                  <p>• Spotify: Share → Embed → Copy code → Extract URL</p>
                  <p>• SoundCloud: Share → Embed → Copy URL</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Floating Mini Player */}
      {miniPlayerOpen && (
        <div
          ref={miniPlayerRef}
          className={`fixed z-[9999] rounded-xl border shadow-2xl overflow-hidden transition-all duration-200 ${
            isDark 
              ? 'bg-slate-900 border-slate-700' 
              : 'bg-white border-slate-200'
          }`}
          style={{
            left: miniPlayerPosition.x,
            top: miniPlayerPosition.y,
            width: miniPlayerMinimized ? 280 : miniPlayerSize.width,
            height: miniPlayerMinimized ? 56 : miniPlayerSize.height,
            boxShadow: isDark 
              ? '0 0 40px rgba(59, 130, 246, 0.3), 0 25px 50px -12px rgba(0, 0, 0, 0.7)' 
              : '0 25px 50px -12px rgba(0, 0, 0, 0.35)'
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Mini Player Header */}
          <div className={`flex items-center justify-between p-2 cursor-move drag-handle ${
            isDark ? 'bg-slate-800' : 'bg-slate-100'
          }`}>
            <div className="flex items-center gap-2">
              <GripVertical className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              {activeTab === 'youtube' ? (
                <Youtube className="h-4 w-4 text-red-500" />
              ) : (
                <Music className="h-4 w-4 text-green-500" />
              )}
              <span className={`text-sm font-medium truncate max-w-[120px] ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}>
                {activeTab === 'youtube' ? 'YouTube' : musicService}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => setMiniPlayerMinimized(!miniPlayerMinimized)}
              >
                {miniPlayerMinimized ? (
                  <Maximize2 className="h-3 w-3" />
                ) : (
                  <Minimize2 className="h-3 w-3" />
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0 hover:bg-red-500/20 hover:text-red-500"
                onClick={closeMiniPlayer}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Mini Player Content */}
          {!miniPlayerMinimized && (
            <div className="flex-1 overflow-hidden" style={{ height: miniPlayerSize.height - 56 }}>
              {activeTab === 'youtube' && youtubeVideoId ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&enablejsapi=1`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : activeTab === 'music' && loadedMusicEmbed ? (
                <iframe
                  src={loadedMusicEmbed}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    No content loaded
                  </p>
                </div>
              )}

              {/* Resize Handle */}
              <div 
                className={`absolute bottom-0 right-0 w-4 h-4 cursor-se-resize ${
                  isDark ? 'text-slate-600' : 'text-slate-300'
                }`}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setIsResizing(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
                </svg>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MediaHub;
